// src/pages/DeleteAccount.tsx — matches the apps' "type DELETE to confirm" flow
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, AlertTriangle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { apiService } from "../services/api";

export default function DeleteAccount() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const canDelete = confirmText.trim() === "DELETE";

  const doDelete = async () => {
    if (!canDelete) return;
    setBusy(true);
    setErr(null);
    try {
      await apiService.deleteAccount();
      await logout();
      navigate("/login", { replace: true });
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || "Could not delete account");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-xl mx-auto px-5 pt-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-gray-400 mb-6">
          <ChevronLeft size={20} /> Back
        </button>

        <div className="flex items-center gap-2 mb-4 text-red-400">
          <AlertTriangle size={22} />
          <h1 className="text-2xl font-semibold">Delete Account</h1>
        </div>

        <p className="text-gray-400 mb-2">
          This action cannot be undone. Your account data will be permanently deleted.
        </p>
        <p className="text-gray-500 text-sm mb-6">
          Blockchain records will remain as they are immutable.
        </p>

        <label className="block text-sm text-gray-400 mb-1">
          Type <span className="text-red-400 font-semibold">DELETE</span> to confirm
        </label>
        <input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="DELETE"
          className="w-full mb-4 px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-red-500 outline-none tracking-widest"
        />

        {err && <div className="mb-4 text-sm text-red-400">{err}</div>}

        <button
          onClick={doDelete}
          disabled={!canDelete || busy}
          className="w-full py-3 rounded-xl bg-red-600 hover:bg-red-500 disabled:opacity-40 font-semibold"
        >
          {busy ? "Deleting…" : "Permanently Delete Account"}
        </button>
      </div>
    </div>
  );
}
